import Color from 'color';
import { ref as $ref, atom as $atom } from '@graphistry/falcor-json-graph';
import { opt } from '../views';

import { BLEND_MODES } from '../../../shared/consts';

export const SCENE_SETTINGS_PRUNE_ORPHANS = 2;

/*
const BLEND_MODES = [
    'normal',
    'color',
    'color-burn',
    'color-dodge',
    'darken',
    'difference',
    'hue',
    'exclusion',
    'overlay',
    'lighten',
    'luminosity',
    'multiply',
    'saturation',
    'screen'
];
*/
//NOTE: Likely duplicate in:
//  viz: client/falcor/ClientAPIRoutes, clientParamSafeList, containers/scene, schema/scene, server/routes::sendPostVGraphLoadedUpdate 
//  forge-etl-python: upload validate_metadata
//  nexus: viz/models::SessionSnapshotState
//  streamgl: models/src/SessionSnaptshotState
//NOTE: Defaults reset during workbook model dbDeserialize (viz_dataset + viz_snapshotstatesession)
// Validation cleaning must therefore be duplicated by forge-etl-python::upload::validate_metadata (=> viz_dataset)
export function scene(view, sceneID = 'default', allOptions = {}) {

    const options = allOptions.vizClientParams || {};

    function clamp(v, low, high) {
        return Math.max(Math.min(v, high), low);
    }

    return {
        scene: {
            id: sceneID,
            name: 'Scene',
            simulating: false,

            axis: $ref(`${view}.axis`),
            camera: $ref(`${view}.camera`),
            labels: $ref(`${view}.labels`),
            release: $ref(`release.current`),
            highlight: $ref(`${view}.highlight`),
            selection: $ref(`${view}.selection`),

            fg: {
                blendMode: opt(options, 'fgBlendMode', BLEND_MODES),
            },

            bg: {
                color: $ref(`${view}.scene.renderer.background`),
                image: {
                    blendMode: opt(options, 'bgImageBlendMode', BLEND_MODES),
                    width:  options['bgImageWidth'], //null, 40, '100%',
                    height: options['bgImageHeight'], //null, 40, '100%',
                    url:    options['bgImageUrl'],
                },
                gradient: {
                   kind: opt(options, 'bgGradientKind', ['linear', 'radial']),
                   position: opt(options, 'bgGradientPosition', String),
                   stops: options['bgGradientStops'] ? $atom(JSON.parse(options['bgGradientStops'])) : null
                }
            },

            //what about hydration from save?
            logo: {
                url:        opt(options, 'logoUrl', String, null),
                autoInvert: opt(options, 'logoAutoInvert', Boolean, false),
                position:   opt(options, 'logoPosition', ['top', 'top-left', 'left', 'bottom-left', 'bottom', 'bottom-right', 'right', 'top-right'], 'bottom'),
                dimensions: {
                    maxWidth:  clamp(opt(options, 'logoMaxWidth',  Number, 150), 0, 150),
                    maxHeight: clamp(opt(options, 'logoMaxHeight', Number, 150), 0, 150),
                },
                crop: {
                    top:    opt(options, 'logoCropTop',    Number, 0),
                    left:   opt(options, 'logoCropLeft',   Number, 0),
                    bottom: opt(options, 'logoCropBottom', Number, 0),
                    right:  opt(options, 'logoCropRight',  Number, 0)
                },
                padding: {
                    top:    opt(options, 'logoPaddingTop',    Number, 0),
                    left:   opt(options, 'logoPaddingLeft',   Number, 0),
                    bottom: opt(options, 'logoPaddingBottom', Number, 0),
                    right:  opt(options, 'logoPaddingRight',  Number, 0)
                },
                style: {
                    brightness: clamp(opt(options, 'logoBrightness', Number, 1), 0, 1),
                    contrast:   clamp(opt(options, 'logoContrast',   Number, 1), 0, 1),
                    grayscale:  clamp(opt(options, 'logoGrayscale',  Number, 0), 0, 1),
                    hueRotate:  clamp(opt(options, 'logoHueRotate',  Number, 0), 0, 360),
                    opacity:    clamp(opt(options, 'logoOpacity',    Number, 1), 0, 1),
                    saturate:   clamp(opt(options, 'logoSaturate',   Number, 1), 0, 1)
                }
            },

            page: {
                favicon: opt(options, 'favicon', String),
                title: opt(options, 'pageTitle', String) //"asdf -- {{ name }}"
            },

            renderer: {
                id: sceneID,
                showArrows: opt(options, 'showArrows', Boolean, true),
                camera: $ref(`${view}.camera`),
                layout: $ref(`${view}.layout`),
                highlight: $ref(`${view}.highlight`),
                selection: $ref(`${view}.selection`),
                foreground: { color: new Color(`#ffffff`) },
                background: { color: new Color(options.bg || '#323238') },
                edges: { 
                    scaling: 1,
                    opacity: opt(options, 'edgeOpacity', Number, 1),
                    elements: undefined,
                    curvature: opt(options, 'edgeCurvature', Number, 0.2)
                },
                points: {
                    scaling: opt(options, 'pointSize', Number, 1),
                    opacity: opt(options, 'pointOpacity', Number, 1),
                    neighborhoodHighlight: opt(options, 'neighborhoodHighlight', String, 'both'),
                    neighborhoodHighlightHops: opt(options, 'neighborhoodHighlightHops', Number, 1),
                    stroke: opt(options, 'pointStrokeWidth', Number, 4),
                    elements: undefined
                },
                labels: $ref(`${view}.labels`)
            },

            controls: [
                {
                    selected: false,
                    id: 'toggle-simulating',
                    name: 'Toggle visual clustering',

                    //should be from config, but cannot include here -- passed in from routes.js
                    timeoutMS: opt(allOptions, 'graph-play-timeoutms', Number, 60000)
                },
                {
                    selected: false,
                    id: 'toggle-scene-settings',
                    name: 'Scene settings',
                    type: 'settings'
                }
            ],
            settings: [
                {
                    id: 'scene',
                    length: 11,
                    ...[
                        $ref(`${view}.scene.options['background-color']`),
                        // $ref(`${view}.scene.options['point-colors']`),
                        $ref(`${view}.scene.options['show-arrows']`),
                        $ref(`${view}.scene.options['prune-orphans']`),
                        $ref(`${view}.scene.options['point-stroke-width']`),
                        $ref(`${view}.scene.options['point-size']`),
                        $ref(`${view}.scene.options['edge-size']`),
                        $ref(`${view}.scene.options['edge-curvature']`),
                        $ref(`${view}.scene.options['point-opacity']`),
                        $ref(`${view}.scene.options['edge-opacity']`),
                        $ref(`${view}.scene.options['neighborhood-highlight']`),
                        $ref(`${view}.scene.options['neighborhood-highlight-hops']`)
                    ]
                }
            ],
            options: {
                'point-colors': {
                    id: 'point-colors',
                    type: 'color',
                    name: 'Override Point Colors',
                    value: $ref(`${view}.scene.renderer.foreground.color`)
                },
                'background-color': {
                    id: 'background-color',
                    type: 'color',
                    name: 'Background Color',
                    value: $ref(`${view}.scene.renderer.background.color`)
                },
                'show-arrows': {
                    id: 'show-arrows',
                    type: 'bool',
                    name: 'Show Arrows',
                    value: $ref(`${view}.scene.renderer.showArrows`)
                },
                'prune-orphans': {
                    id: 'prune-orphans',
                    type: 'bool',
                    name: 'Hide Standalone Nodes',
                    value: $ref(`${view}.pruneOrphans`)
                },
                'neighborhood-highlight': {
                    id: 'neighborhood-highlight',
                    type: 'dropdown',
                    name: 'Highlight node neighbors',
                    props: {
                        'incoming': 'Highlight incoming edges',
                        'outgoing': 'Highlight outgoing edges',
                        'both': 'Highlight both incoming & outgoing edges',
                        'none': 'Do not highlight neighbors'
                    },
                    value: $ref(`${view}.scene.renderer.points.neighborhoodHighlight`)
                },
                'neighborhood-highlight-hops': {
                    'id': 'neigborhood-highlight-hops',
                    type: 'discrete',
                    name: 'Highlight hops away',
                    props: {
                        min: 0,
                        max: 10,
                    },
                    value: $ref(`${view}.scene.renderer.points.neighborhoodHighlightHops`)
                },
                'point-stroke-width': {
                    id: 'point-stroke-width',
                    type: 'discrete',
                    name: 'Point Stroke Width',
                    props: {
                        min: 0,
                        max: 10,
                        step: 0.1,
                    },
                    value: $ref(`${view}.scene.renderer.points.stroke`)
                },
                'point-size': {
                    id: 'point-size',
                    type: 'discrete',
                    name: 'Point Size',
                    props: {
                        min: 1,
                        max: 100,
                        step: 1,
                        scale: 'log'
                    },
                    value: $ref(`${view}.scene.renderer.points.scaling`)
                },
                'edge-size': {
                    id: 'edge-size',
                    type: 'discrete',
                    name: 'Edge Size',
                    props: {
                        min: 1,
                        max: 100,
                        step: 1,
                        scale: 'log'
                    },
                    value: $ref(`${view}.scene.renderer.edges.scaling`)
                },
                'edge-curvature': {
                    id: 'edge-curvature',
                    type: 'discrete',
                    name: 'Edge Curvature',
                    props: {
                        min: 0,
                        max: 100,
                        step: 1,
                        scale: 'percent'
                    },
                    value: $ref(`${view}.scene.renderer.edges.curvature`)
                },
                'point-opacity': {
                    id: 'point-opacity',
                    type: 'discrete',
                    name: 'Point Opacity',
                    props: {
                        min: 0,
                        max: 100,
                        step: 1,
                        scale: 'percent'
                    },
                    value: $ref(`${view}.scene.renderer.points.opacity`)
                },
                'edge-opacity': {
                    id: 'edge-opacity',
                    type: 'discrete',
                    name: 'Edge Opacity',
                    props: {
                        min: 0,
                        max: 100,
                        step: 1,
                        scale: 'percent'
                    },
                    value: $ref(`${view}.scene.renderer.edges.opacity`)
                }
            }
        }
    };
}
